import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Panel } from 'react-bootstrap';
import ReactEcharts from 'echarts-for-react';

import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';
import { exportAsExcel } from '../../Services/exportAsExcel';

const AGE_BUCKETS = [1, 3, 6, 12, 18, 24];
const WINDOW_OPTIONS = [30, 60, 90, 180];

const fmtEur = (n) => n == null ? '—' : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
const fmtEurPrecise = (n) => n == null ? '—' : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtPct = (n) => n == null ? '—' : `${(n * 100).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
const fmtNum = (n, d = 2) => n == null ? '—' : n.toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d });

const kpiStyle = (color) => ({
    flex: '1 1 180px',
    minWidth: 180,
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderLeft: `4px solid ${color}`,
});

const channelColor = {
    google_ads: '#4285F4',
    skycall: '#F4B400',
    organic_search: '#0F9D58',
    blog: '#9C27B0',
    pharmacy: '#00BCD4',
    direct: '#9E9E9E',
    referral: '#E91E63',
    all: '#546E7A',
};
const colorFor = (ch) => channelColor[ch] || '#607D8B';

const defaultStart = () => {
    const d = new Date();
    d.setUTCMonth(d.getUTCMonth() - 12);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const currentMonth = () => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export default function LTVClient() {
    const [startMonth, setStartMonth] = useState(defaultStart());
    const [endMonth, setEndMonth] = useState(currentMonth());
    const [activeWindowDays, setActiveWindowDays] = useState(90);
    const [ageBucketMonths, setAgeBucketMonths] = useState(24);
    const [channelBreakdown, setChannelBreakdown] = useState(false);

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState(null);
    const [assumptions, setAssumptions] = useState(null);
    const [showAssumptions, setShowAssumptions] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        fetchApi('/users/analytics/ltv-v2', {
            method: 'POST',
            body: JSON.stringify({
                startMonth,
                endMonth,
                activeWindowDays,
                channelBreakdown,
                ageBucketMonths,
            }),
        })
            .then((json) => {
                setRows(json.results || []);
                setTotals(json.totals || null);
                setAssumptions(json.assumptions || null);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                notif.addNotification('Erreur', 'LTV: ' + (err && err.message ? err.message : err), 'warning');
            });
    }, [startMonth, endMonth, activeWindowDays, channelBreakdown, ageBucketMonths]);

    useEffect(() => { load(); }, [load]);

    const headlineKey = `m${ageBucketMonths}`;

    // Chart: observed LTV curve per channel (or blended), averaged across cohort rows weighted by newCustomers.
    const chartOption = useMemo(() => {
        if (!rows.length) return null;
        const channels = channelBreakdown
            ? [...new Set(rows.map(r => r.channel))]
            : ['all'];
        const series = channels.map(ch => {
            const rs = rows.filter(r => r.channel === ch);
            const data = AGE_BUCKETS.map(m => {
                const k = `m${m}`;
                const valid = rs.filter(r => r.observedLTV[k] != null);
                const total = valid.reduce((s, r) => s + r.newCustomers, 0);
                if (total === 0) return null;
                const v = valid.reduce((s, r) => s + r.observedLTV[k] * r.newCustomers, 0) / total;
                return Number(v.toFixed(2));
            });
            return {
                name: ch,
                type: 'line',
                smooth: true,
                connectNulls: true,
                symbolSize: 7,
                lineStyle: { width: 2.5 },
                itemStyle: { color: colorFor(ch) },
                data,
            };
        });
        return {
            tooltip: { trigger: 'axis', valueFormatter: (v) => v == null ? '—' : `${Math.round(v)} €` },
            legend: { top: 0 },
            grid: { top: 32, right: 16, bottom: 24, left: 56 },
            xAxis: {
                type: 'category',
                data: AGE_BUCKETS.map(m => `M${m}`),
                name: 'Âge cohorte',
            },
            yAxis: { type: 'value', name: 'LTV observée (€)' },
            series,
        };
    }, [rows, channelBreakdown]);

    const exportRows = () => {
        const data = rows.map(r => {
            const base = {
                Mois: r.month,
                Canal: r.channel,
                'Nouveaux clients': r.newCustomers,
                'Consultations': r.consultations,
                'CA (€)': r.caTotal,
                'ARPU (€)': r.arpu,
                'Rétention': r.retention,
                'Churn': r.churnRate,
                'Durée vie (mois)': r.lifetimeMonths,
            };
            AGE_BUCKETS.forEach(m => { base[`LTV obs M${m} (€)`] = r.observedLTV[`m${m}`]; });
            base['LTV projetée (€)'] = r.projectedLTV;
            base['Frais Stripe / client (€)'] = r.stripeFeesPerCustomer;
            base['Rémunération médecin / client (€)'] = r.doctorFeesPerCustomer;
            base['CAC (€)'] = r.cac;
            base['LTV/CAC'] = r.ltvCacRatio;
            base['Payback (mois)'] = r.paybackMonths;
            base['LTV nette (€)'] = r.netLTV;
            return base;
        });
        const cols = [{ wch: 10 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 12 }];
        exportAsExcel(data, `ltv_${startMonth}_${endMonth}`, cols);
    };

    return (
        <Panel>
            <Modal
                show={loading}
                backdrop="static"
                keyboard={false}
                size="lg"
                className="modal-spinner"
            >
                <span className="loader" />
                {loading && <p>Calcul de la LTV…</p>}
            </Modal>

            <Panel.Heading>
                <h6 style={{ margin: 0 }}>LTV par cohorte {channelBreakdown ? '× canal' : ''}</h6>
            </Panel.Heading>

            <Panel.Body>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
                        Du (mois)
                        <input
                            type="month"
                            value={startMonth}
                            onChange={(e) => setStartMonth(e.target.value)}
                            style={{ padding: 6, border: '1px solid #ccc', borderRadius: 4 }}
                        />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
                        Au (mois)
                        <input
                            type="month"
                            value={endMonth}
                            onChange={(e) => setEndMonth(e.target.value)}
                            style={{ padding: 6, border: '1px solid #ccc', borderRadius: 4 }}
                        />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
                        Fenêtre active (jours)
                        <select
                            value={activeWindowDays}
                            onChange={(e) => setActiveWindowDays(parseInt(e.target.value, 10))}
                            style={{ padding: 6, border: '1px solid #ccc', borderRadius: 4 }}
                        >
                            {WINDOW_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
                        LTV observée à
                        <select
                            value={ageBucketMonths}
                            onChange={(e) => setAgeBucketMonths(parseInt(e.target.value, 10))}
                            style={{ padding: 6, border: '1px solid #ccc', borderRadius: 4 }}
                        >
                            {AGE_BUCKETS.map(m => <option key={m} value={m}>M{m}</option>)}
                        </select>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={channelBreakdown}
                            onChange={(e) => setChannelBreakdown(e.target.checked)}
                        />
                        Répartir par canal
                    </label>
                    <button
                        onClick={load}
                        style={{ padding: '8px 16px', backgroundColor: '#00c1c1', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                        Recalculer
                    </button>
                    <button
                        onClick={exportRows}
                        disabled={!rows.length}
                        style={{ padding: '8px 16px', backgroundColor: '#607D8B', color: '#fff', border: 'none', borderRadius: 4, cursor: rows.length ? 'pointer' : 'not-allowed', opacity: rows.length ? 1 : 0.5 }}
                    >
                        Export Excel
                    </button>
                </div>

                {totals && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                        <div
                            style={{ ...kpiStyle('#4285F4'), cursor: 'help' }}
                            title={"Nombre de clients ayant passé leur première commande payée (approved / terminated / reviewed) sur la période sélectionnée.\n\nChaque client est compté une seule fois, dans le mois de sa première commande."}
                        >
                            <div style={{ fontSize: 12, color: '#666' }}>Nouveaux clients <span style={{ color: '#999' }}>ⓘ</span></div>
                            <div style={{ fontSize: 22, fontWeight: 600 }}>{totals.newCustomers.toLocaleString('fr-FR')}</div>
                        </div>
                        <div
                            style={{ ...kpiStyle('#0F9D58'), cursor: 'help' }}
                            title={"Chiffre d'affaires total généré par les nouveaux clients de la période (toutes commandes payées confondues, pendant toute leur durée de vie observée)."}
                        >
                            <div style={{ fontSize: 12, color: '#666' }}>CA total <span style={{ color: '#999' }}>ⓘ</span></div>
                            <div style={{ fontSize: 22, fontWeight: 600 }}>{fmtEur(totals.caTotal)}</div>
                        </div>
                        <div
                            style={{ ...kpiStyle('#F4B400'), cursor: 'help' }}
                            title={"ARPU = Average Revenue Per User.\n\nCA total ÷ nombre de nouveaux clients.\n« Pondérée » car calculée sur l'ensemble des clients, pas en moyenne des cohortes."}
                        >
                            <div style={{ fontSize: 12, color: '#666' }}>ARPU (pondérée) <span style={{ color: '#999' }}>ⓘ</span></div>
                            <div style={{ fontSize: 22, fontWeight: 600 }}>{fmtEurPrecise(totals.arpu)}</div>
                        </div>
                        <div
                            style={{ ...kpiStyle('#E91E63'), cursor: 'help' }}
                            title={`Revenu cumulé moyen par client ${ageBucketMonths} mois après sa première commande.\n\nC'est la LTV réellement mesurée (pas une projection). Seules les cohortes assez anciennes pour avoir atteint M${ageBucketMonths} sont prises en compte.`}
                        >
                            <div style={{ fontSize: 12, color: '#666' }}>LTV obs. M{ageBucketMonths} <span style={{ color: '#999' }}>ⓘ</span></div>
                            <div style={{ fontSize: 22, fontWeight: 600 }}>{fmtEurPrecise(totals.headlineObservedLTV)}</div>
                        </div>
                        <div
                            style={{ ...kpiStyle('#9C27B0'), cursor: 'help' }}
                            title={"LTV nette = LTV observée (ou projetée à défaut) − frais Stripe/client − rémunération médecin/client − CAC.\n\nLa valeur affichée ici est la moyenne pondérée par la taille des cohortes."}
                        >
                            <div style={{ fontSize: 12, color: '#666' }}>LTV nette pondérée <span style={{ color: '#999' }}>ⓘ</span></div>
                            <div style={{ fontSize: 22, fontWeight: 600 }}>{fmtEurPrecise(totals.netLTV)}</div>
                        </div>
                        <div
                            style={{ ...kpiStyle('#546E7A'), cursor: 'help' }}
                            title={"Dépense publicitaire Google Ads sur la période (source GA4, métrique advertiserAdCost).\n\nSert au calcul du CAC pour le canal google_ads : dépense du mois ÷ clients acquis ce mois via google_ads."}
                        >
                            <div style={{ fontSize: 12, color: '#666' }}>Dépense Google Ads <span style={{ color: '#999' }}>ⓘ</span></div>
                            <div style={{ fontSize: 22, fontWeight: 600 }}>{fmtEur(totals.googleAdsSpend)}</div>
                        </div>
                    </div>
                )}

                {chartOption && (
                    <div style={{ marginBottom: 20 }}>
                        <h6 style={{ margin: '8px 0' }}>LTV observée par âge de cohorte</h6>
                        <ReactEcharts option={chartOption} style={{ height: 320 }} />
                    </div>
                )}

                <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th title="Mois de la première commande payée du client (cohorte d'acquisition).">Mois</th>
                                {channelBreakdown && <th title="Canal d'acquisition du client, déduit de la 1ʳᵉ request : gclid/utm_source=google → google_ads ; createdBySkycall → skycall ; utm_source=feeli-blog → blog ; etc.">Canal</th>}
                                <th title="Nombre de clients ayant passé leur 1ʳᵉ commande payée ce mois-là (et sur ce canal si le split est activé).">Nouveaux</th>
                                <th title="Chiffre d'affaires total généré par ces clients (toutes leurs commandes payées, pas seulement la 1ʳᵉ).">CA</th>
                                <th title="CA ÷ nombre de nouveaux clients. Revenu moyen par client sur toute leur durée observée.">ARPU</th>
                                <th title={`% de clients ayant passé au moins une 2ᵉ commande dans les ${assumptions ? assumptions.activeWindowDays : '?'} jours suivant leur 1ʳᵉ commande.`}>Rétention</th>
                                <th title="Taux de churn = 1 − rétention. Part des clients qui ne sont pas revenus dans la fenêtre d'activité.">Churn</th>
                                <th title={`Durée de vie estimée = (fenêtre active / 30,44) / churn, plafonnée à ${assumptions ? assumptions.maxLifetimeMonths : '?'} mois. Utilisée uniquement pour la LTV projetée.`}>Durée (mois)</th>
                                <th title={`Revenu cumulé moyen par client, M${ageBucketMonths} après sa 1ʳᵉ commande. LTV réellement mesurée. Vide si la cohorte n'a pas encore atteint cet âge.`}>LTV obs. M{ageBucketMonths}</th>
                                <th title="ARPU × durée de vie estimée. Projection théorique, pas une mesure — à utiliser surtout pour les cohortes récentes.">LTV projetée</th>
                                <th title="Frais Stripe moyens par client = Σ (1,5 % × paidAmount + 0,25 €) ÷ nombre de clients.">Stripe/client</th>
                                <th title="Rémunération médecin moyenne par client = Σ payouts ÷ nombre de clients. Dermato/acné : 20 € ; autres types : 15 €.">Médecin/client</th>
                                {channelBreakdown && <th title="Coût d'Acquisition Client = dépense Google Ads du mois ÷ clients acquis ce mois via google_ads. Disponible uniquement pour le canal google_ads.">CAC</th>}
                                {channelBreakdown && <th title="Ratio LTV observée ÷ CAC. Repères : ≥ 3 bon (vert), 1–3 correct (jaune), < 1 non rentable (rouge).">LTV/CAC</th>}
                                {channelBreakdown && <th title="Temps de retour sur investissement : premier âge de cohorte (M1/M3/M6/…) où la LTV observée dépasse le CAC.">Payback</th>}
                                <th
                                    style={{ cursor: 'help', textDecoration: 'underline dotted' }}
                                    title={"LTV nette = LTV observée (ou projetée à défaut)\n  − frais Stripe / client\n  − rémunération médecin / client\n  − CAC (uniquement pour le canal google_ads).\n\nVoir « hypothèses de calcul » ci-dessous pour le détail."}
                                >
                                    LTV nette <span style={{ color: '#999', fontWeight: 400 }}>ⓘ</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => {
                                const ratioColor = r.ltvCacRatio == null
                                    ? undefined
                                    : r.ltvCacRatio >= 3 ? '#0F9D58'
                                        : r.ltvCacRatio >= 1 ? '#F4B400'
                                            : '#D32F2F';
                                return (
                                    <tr key={`${r.month}-${r.channel}-${i}`}>
                                        <td>{r.month}</td>
                                        {channelBreakdown && (
                                            <td>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: 10, height: 10, borderRadius: 5,
                                                    backgroundColor: colorFor(r.channel),
                                                    marginRight: 6,
                                                }} />
                                                {r.channel}
                                            </td>
                                        )}
                                        <td>{r.newCustomers}</td>
                                        <td>{fmtEur(r.caTotal)}</td>
                                        <td>{fmtEurPrecise(r.arpu)}</td>
                                        <td>{fmtPct(r.retention)}</td>
                                        <td>{fmtPct(r.churnRate)}</td>
                                        <td>{fmtNum(r.lifetimeMonths, 1)}</td>
                                        <td title={r.matureThrough ? `Mature jusqu'à M${r.matureThrough}` : 'Cohorte pas encore mature'}>
                                            {fmtEurPrecise(r.observedLTV[headlineKey])}
                                        </td>
                                        <td>{fmtEurPrecise(r.projectedLTV)}</td>
                                        <td>{fmtEurPrecise(r.stripeFeesPerCustomer)}</td>
                                        <td>{fmtEurPrecise(r.doctorFeesPerCustomer)}</td>
                                        {channelBreakdown && <td>{fmtEurPrecise(r.cac)}</td>}
                                        {channelBreakdown && (
                                            <td style={{ color: ratioColor, fontWeight: ratioColor ? 600 : undefined }}>
                                                {fmtNum(r.ltvCacRatio, 2)}
                                            </td>
                                        )}
                                        {channelBreakdown && <td>{r.paybackMonths == null ? '—' : `M${r.paybackMonths}`}</td>}
                                        <td style={{ fontWeight: 600 }}>{fmtEurPrecise(r.netLTV)}</td>
                                    </tr>
                                );
                            })}
                            {!rows.length && !loading && (
                                <tr><td colSpan={channelBreakdown ? 16 : 12} style={{ textAlign: 'center', color: '#999', padding: 24 }}>Aucune donnée pour la période.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {assumptions && (
                    <div style={{ marginTop: 20, fontSize: 12, color: '#555' }}>
                        <button
                            onClick={() => setShowAssumptions(s => !s)}
                            style={{ background: 'transparent', border: '1px solid #ccc', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                        >
                            {showAssumptions ? 'Cacher' : 'Voir'} les hypothèses de calcul
                        </button>
                        {showAssumptions && (
                            <div style={{ marginTop: 10, padding: 12, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 4 }}>
                                <p style={{ margin: '4px 0' }}><strong>LTV brute</strong> : somme des paiements réels (paidAmount Stripe) des requests avec état <code>approved / terminated / reviewed</code>, par client, par cohorte d'acquisition (= mois de la première commande payée). Les états <code>canceled / refunded</code> sont exclus.</p>
                                <p style={{ margin: '4px 0' }}><strong>LTV observée M&lt;X&gt;</strong> : revenu cumulé moyen par client, X mois après la première commande. Cellule vide si la cohorte n'a pas encore atteint cet âge.</p>
                                <p style={{ margin: '4px 0' }}><strong>Rétention</strong> : % de clients avec au moins une 2ᵉ commande dans les {assumptions.activeWindowDays} jours suivant la première. Utilisée pour <code>lifetime = (fenêtre / 30.44) / churn</code>, plafonné à {assumptions.maxLifetimeMonths} mois.</p>
                                <p style={{ margin: '4px 0' }}><strong>LTV projetée</strong> : <code>ARPU × durée de vie estimée</code>. Estimation, pas une mesure.</p>
                                <p style={{ margin: '4px 0' }}><strong>Frais Stripe</strong> : {(assumptions.stripePercent * 100).toFixed(2)} % + {assumptions.stripeFlatEur.toFixed(2)} € par transaction.</p>
                                <p style={{ margin: '4px 0' }}><strong>Rémunération médecin</strong> : dermatologie/acné = {assumptions.doctorPayout.dermatologie} € ; autres = {assumptions.doctorPayout._default} €.</p>
                                <p style={{ margin: '4px 0' }}><strong>CAC</strong> : dépense Google Ads (GA4 <code>advertiserAdCost</code>) du mois de cohorte ÷ clients acquis via <code>google_ads</code>. Disponible uniquement pour ce canal.</p>
                                <p style={{ margin: '4px 0' }}><strong>LTV nette</strong> : LTV observée (ou projetée à défaut) − frais Stripe/client − rémunération médecin/client − CAC.</p>
                                <p style={{ margin: '4px 0' }}><strong>Payback</strong> : premier âge de cohorte (M1/M3/M6/…) où LTV observée ≥ CAC.</p>
                                <p style={{ margin: '4px 0', color: '#888' }}>Ces constantes sont définies dans <code>server/Controllers/UsersController.js</code> et peuvent être ajustées.</p>
                            </div>
                        )}
                    </div>
                )}
            </Panel.Body>
        </Panel>
    );
}
